import { createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'
import { Traceable } from '@repo/shared/dtos/assistant'
import { PromptDefaultOwnerOrganisationId } from '@repo/shared/dtos/organization'
import {
  StructuredDataDisplayDTO,
  StructuredDataSchemaDTO,
} from '@repo/shared/dtos/structured-data'

import { AsyncThunkConfig, RejectValue } from '@/store/actions'
import { OrganizationCreationFields, OrganizationFields } from '@/store/spi/api'
import { SudoContextOrganization } from '@/store/sudo'
import { Organization } from '@/store/user'

export const organization = {
  //
  // ADMIN
  //
  admin: {
    updateStructuredDataSchema: createAsyncThunk<
      { organization: Organization },
      { updatedSchema: StructuredDataSchemaDTO },
      AsyncThunkConfig
    >(
      'UPDATE_STRUCTURED_DATA_SCHEMA',
      async (
        { updatedSchema },
        { extra: { api }, rejectWithValue, fulfillWithValue, getState },
      ) => {
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const resSchema = await api.admin.organizations.updateStructuredDataSchema({
          organizationId,
          structuredDataSchema: updatedSchema,
        })
        if (hasError(resSchema)) return rejectWithValue(handleError(resSchema))
        const organization = await api.organizations.getOne(organizationId)
        if (hasError(organization)) return rejectWithValue(handleError(organization))
        return fulfillWithValue({ organization })
      },
    ),

    updateStructuredDataDisplay: createAsyncThunk<
      { organization: Organization },
      { updatedDisplay: StructuredDataDisplayDTO },
      AsyncThunkConfig
    >(
      'UPDATE_STRUCTURED_DATA_DISPLAY',
      async (
        { updatedDisplay },
        { extra: { api }, rejectWithValue, fulfillWithValue, getState },
      ) => {
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
        const resDisplay = await api.admin.organizations.updateStructuredDataDisplay({
          organizationId,
          structuredDataDisplay: updatedDisplay,
        })
        if (hasError(resDisplay)) return rejectWithValue(handleError(resDisplay))
        const organization = await api.organizations.getOne(organizationId)
        if (hasError(organization)) return rejectWithValue(handleError(organization))
        return fulfillWithValue({ organization })
      },
    ),

    generateDescription: createAsyncThunk<
      GenerateDescriptionPayload,
      GenerationArg,
      AsyncThunkConfig
    >(
      'GENERATE_DESCRIPTION',
      async (_, { extra: { api }, rejectWithValue, fulfillWithValue, getState }) => {
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const res = await api.admin.organizations.generateDescriptionForOne(organizationId)
        if (hasError(res)) return rejectWithValue(handleError(res))
        return fulfillWithValue(res)
      },
    ),

    generateMethodology: createAsyncThunk<
      GenerateMethodologyPayload,
      GenerationArg,
      AsyncThunkConfig
    >(
      'GENERATE_METHODOLOGY',
      async (_, { extra: { api }, rejectWithValue, fulfillWithValue, getState }) => {
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const res = await api.admin.organizations.generateMethodologyForOne(organizationId)
        if (hasError(res)) return rejectWithValue(handleError(res))
        return fulfillWithValue(res)
      },
    ),

    update: createAsyncThunk<
      { organization: Organization },
      { fields: OrganizationFields },
      AsyncThunkConfig
    >(
      'UPDATE_ORGANIZATION',
      async ({ fields }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { api } = extra
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const data = await api.admin.organizations.patchOne({
          fields,
          organizationId,
        })
        if (hasError(data)) return rejectWithValue(handleError(data))
        return fulfillWithValue({ organization: data })
      },
    ),
  },

  //
  // SUDO
  //
  sudo: {
    create: createAsyncThunk<
      { organizations: SudoContextOrganization[] },
      OrganizationCreationFields,
      AsyncThunkConfig
    >('SUDO_CREATE_ORGANIZATION', async (payload, { extra, fulfillWithValue, rejectWithValue }) => {
      const { sudoApi } = extra
      const organizations = await sudoApi.organizations.createOne(payload)
      if (hasError(organizations)) return rejectWithValue(handleError(organizations))
      return fulfillWithValue({ organizations })
    }),

    update: createAsyncThunk<
      { organizations: SudoContextOrganization[] },
      {
        organizationId: string
        fields: Partial<Pick<SudoContextOrganization, 'name' | 'lifeCycleStage'>>
      },
      AsyncThunkConfig
    >('SUDO_UPDATE_ORGANIZATION', async (payload, { extra, fulfillWithValue, rejectWithValue }) => {
      const { sudoApi } = extra
      const organizations = await sudoApi.organizations.patchOne(payload)
      if (hasError(organizations)) return rejectWithValue(handleError(organizations))
      return fulfillWithValue({ organizations })
    }),
    hardDeleteOne: createAsyncThunk<
      { success: boolean; errorMessage: string },
      { organizationId: string },
      AsyncThunkConfig
    >(
      'SUDO_HARD_DELETE_ORGANIZATION',
      async (payload, { extra, fulfillWithValue, rejectWithValue }) => {
        const { sudoApi } = extra
        if (payload.organizationId === PromptDefaultOwnerOrganisationId)
          return rejectWithValue(RejectValue.NotAllowed)
        const result = await sudoApi.organizations.hardDeleteOne(payload)
        if (hasError(result)) return rejectWithValue(handleError(result))
        return fulfillWithValue(result)
      },
    ),
  },
}
export type GenerationArg = { generationId: string }
export type GenerateDescriptionPayload = { description: string } & Traceable
export type GenerateMethodologyPayload = { methodology: string } & Traceable
